import { Box, Button, Checkbox, CircularProgress, Divider, FormControlLabel, ITheme, Link, Slider, SvgIcon, TextField, ToggleButton, Tooltip, Typography, useTheme } from '@mui/material'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next';
import NumberInput from '../../common/NumberInput';
import IndustrySelector from './IndustrySelector';
import SplitSelector from './SplitSelector';
import StageSelector from './StageSelector';
import CountrySelector from './CountrySelector';
import { ReactComponent as questionIcon } from '../../../assets/question.svg'
import { useAppDispatch, useAppSelector } from '../../../store/reducers/store';
import { affectPortfolio, saveDraft, updatePortfolio } from '../../../store/actions/createPortfolio';
import { StageDistribution } from '../../../models/Common';
import { CountryAffectation, PortfolioAffectation, SectorAffectation, SplitAffectation } from '../../../models/Portfolio';
import { useNavigate, useParams } from 'react-router-dom';
import { AUTHENTICATED_ROUTES } from '../../../resources/routes-constants';

const getNumberOfFund = (foundCount: number): number => {
    if (isNaN(foundCount) || [0, 5, 10, 15, 20, 25].indexOf(foundCount) !== -1) {
        return foundCount;
    }
    return 0;
}

const CreateFirstStep = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const theme = useTheme() as ITheme;
    const { id } = useParams();
    const portfolio = useAppSelector(state => state.createPortfolio.portfolio);
    // const [sliderRange, setSliderRange] = useState(portfolio ? portfolio.fundraisingTarget : [0, 100000000]);
    const [selectedCount, setSelectedCount] = useState(portfolio ? getNumberOfFund(portfolio.fundCount) : NaN);
    const [isSubmitting, setIsSubmitting] = useState<number | undefined>(undefined);
    const [showMinTargetError, setShowMinTargerError] = useState(false);
    const { t, i18n } = useTranslation();

    const valuetext = (value: number) => {
        return `€${value.toLocaleString(i18n.language)}`;
    }

    const onSliderChanged = (event: Event, gap: number | number[]): void => {
        dispatch(updatePortfolio({ field: 'fundraisingTarget', value: gap as number[] }));
    }

    const onFundraisingChanged = (value: number | undefined): void => {
        let update = value
        if (update === undefined) {
            update = 0;
        }
        dispatch(updatePortfolio({ field: 'fundraisingTarget', value: update }));
    }

    const onFundraisingFocused = () => {
        setShowMinTargerError(false);
        console.log("Hide error")
    }
    const onFundraisingBlurred = () => {
        if (portfolio.fundraisingTarget < 1000000) {
            setShowMinTargerError(true);
            console.log("Show error")
            // dispatch(updatePortfolio({ field: 'fundraisingTarget', value: portfolio.fundraisingTarget * 10000000 }));
        }
    }

    const onNameChanged = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        dispatch(updatePortfolio({ field: 'name', value: event.currentTarget.value as string }));
    }
    const onSelectedCount = (event: any, value: any): void => {
        setSelectedCount(value);
        dispatch(updatePortfolio({ field: 'fundCount', value: value }));
    }
    const onManualCount = (event: any, value: any): void => {
        dispatch(updatePortfolio({ field: 'fundCount', value: value }));
    }
    const onDistribChanged = (distrib: StageDistribution): void => {
        dispatch(updatePortfolio({ field: 'stage', value: distrib }));
    }

    const onSectorsChanged = (sectors: SectorAffectation[]): void => {
        dispatch(updatePortfolio({ field: 'sectors', value: sectors }));
    }

    const onSplitChanged = (splits: SplitAffectation[]): void => {
        dispatch(updatePortfolio({ field: 'splits', value: splits }));
    }

    const onCountriesChanged = (countries: CountryAffectation[]): void => {
        dispatch(updatePortfolio({ field: 'countries', value: countries }));
    }

    const onAccreditedInvestorsChanged = (): void => {
        dispatch(updatePortfolio({ field: 'accreditedInvestors', value: !portfolio.accreditedInvestors }));
    }

    const onRetailInvestorsChanged = (): void => {
        dispatch(updatePortfolio({ field: 'retailInvestors', value: !portfolio.retailInvestors }));
    }

    const onStartAi = () => {
        setIsSubmitting(0);
        dispatch(saveDraft()).then(({ payload }) => {
            dispatch(affectPortfolio(PortfolioAffectation.Ai)).then(() => {
                if (payload) {
                    navigate(AUTHENTICATED_ROUTES.PORTFOLIO_EDIT_ROUTE.replace(":id", `${payload}`), { replace: true });
                }
                navigate((payload !== undefined ? AUTHENTICATED_ROUTES.PORTFOLIO_EDIT_STEP_ROUTE.replace(":id", `${payload}`) : AUTHENTICATED_ROUTES.PORTFOLIO_CREATE_STEP_ROUTE).replace(":step", '1'));
            });
        });
    }
    const onStartManually = () => {
        setIsSubmitting(1);
        dispatch(updatePortfolio({ field: 'step', value: 2 }));
        dispatch(saveDraft()).then(({ payload }) => {
            dispatch(affectPortfolio(PortfolioAffectation.Ai)).then(() => {
                if (payload) {
                    navigate(AUTHENTICATED_ROUTES.PORTFOLIO_EDIT_ROUTE.replace(":id", `${payload}`), { replace: true });
                }
                navigate((payload !== undefined ? AUTHENTICATED_ROUTES.PORTFOLIO_EDIT_STEP_ROUTE.replace(":id", `${payload}`) : AUTHENTICATED_ROUTES.PORTFOLIO_CREATE_STEP_ROUTE).replace(":step", '2'));
            });
        });
    }

    const onSaveDraft = () => {
        setIsSubmitting(2);
        dispatch(saveDraft()).then(() => {
            navigate(AUTHENTICATED_ROUTES.PORTFOLIOS_CONFIRM_SAVE_ROUTE.replace(":confirmSave", "saved"));
        })
    }

    if (portfolio === undefined) {
        return <></>
    }

    const blockSubmit = (isSubmitting !== undefined) || !portfolio.name || portfolio.fundraisingTarget < 1000000;
    return (
        <Box display={"flex"} flexDirection={"column"} gap={"40px"} paddingBottom={"50px"}>
            <Box display={"flex"} width={"100%"}>
                <Typography fontSize={"24px"} fontWeight={"500"} lineHeight={"29.26px"}>{t('CreatePortfolioPageTitle')}</Typography>
            </Box>
            <Box display={"flex"} gap={"84px"}>
                <Box flexGrow={1} flexBasis={"200px"} display={"flex"} flexDirection={"column"} gap={"40px"}>
                    <Box display={"flex"} flexDirection={"column"} gap={"12px"}>
                        <Typography fontSize={"16px"} fontWeight={"500"} lineHeight={"19.5px"}>{t('CreatePortfolioNameLabel')}</Typography>
                        <TextField placeholder={t('CreatePortfolioNameLabel')} onChange={onNameChanged} value={portfolio.name} aria-label={t('CreatePortfolioNameLabel')} />
                    </Box>

                    <Box display={"flex"} flexDirection={"column"} gap={"12px"}>
                        <Typography fontSize={"16px"} fontWeight={"500"} lineHeight={"19.5px"}>{t('CreatePortfolioFundraisingTargetLabel')}</Typography>
                        {/* <Slider
                            getAriaLabel={() => t('CreatePortfolioFundraisingTargetPlaceholder')}
                            value={portfolio.fundraisingTarget}
                            onChange={onSliderChanged}
                            valueLabelDisplay="on"
                            getAriaValueText={valuetext}
                            valueLabelFormat={valuetext}
                            max={100000000}
                            min={0}
                            step={100000}
                            sx={{ paddingTop: "60px"}}
                        /> */}
                        <Box display={"flex"} gap={"30px"}>
                            <Tooltip
                                componentsProps={{
                                    arrow: {
                                        style: {
                                            color: theme.palette.leo.color2
                                        }
                                    },
                                    tooltip: {
                                        style: {
                                            backgroundColor: theme.palette.leo.color2
                                        }
                                    }
                                }}
                                open={showMinTargetError}
                                placement='bottom-end'
                                disableFocusListener
                                disableHoverListener
                                disableTouchListener
                                title={
                                    <Box display={"flex"} flexDirection={"column"} justifyContent={"center"} alignItems={"center"} padding={"12px"} gap={"12px"}>
                                        <Typography fontWeight={700} fontSize={"12px"} lineHeight={"18px"} color={theme.palette.common.white}>{t('CreatePortfolioFundraisingTargerTooLowTitle')}</Typography>
                                        <Typography fontWeight={500} fontSize={"12px"} lineHeight={"18px"} color={theme.palette.common.white}>{t('Your current fundraising targer is too low, the minimum allowed is 1,000,000€')}</Typography>
                                    </Box>}
                                arrow={true}>
                                <div>
                                    <NumberInput placeholder={t('CreatePortfolioFundraisingTargetMinPlaceholder')} value={portfolio.fundraisingTarget > 0 ? portfolio.fundraisingTarget : undefined} onChange={(e, value) => onFundraisingChanged(value)} startAdornment='€' onBlur={onFundraisingBlurred} onFocus={onFundraisingFocused} max={1000000000} />
                                </div>
                            </Tooltip>
                        </Box>
                    </Box>

                    <Box display={"flex"} flexDirection={"column"} gap={"12px"}>
                        <Typography fontSize={"16px"} fontWeight={"500"} lineHeight={"19.5px"}>{t('CreatePortfolioNumberOfFundsLabel')}</Typography>
                        <Box display={"flex"} gap={"17px"} flexWrap={"wrap"}>
                            <ToggleButton value={NaN} selected={isNaN(selectedCount)} onClick={onSelectedCount} sx={{ minWidth: "78px" }}>{t('CreatePortfolioAnyLabel')}</ToggleButton>
                            <ToggleButton value={5} selected={selectedCount === 5} onClick={onSelectedCount} sx={{ minWidth: "78px" }}>5</ToggleButton>
                            <ToggleButton value={10} selected={selectedCount === 10} onClick={onSelectedCount} sx={{ minWidth: "78px" }}>10</ToggleButton>
                            <ToggleButton value={15} selected={selectedCount === 15} onClick={onSelectedCount} sx={{ minWidth: "78px" }}>15</ToggleButton>
                            <ToggleButton value={20} selected={selectedCount === 20} onClick={onSelectedCount} sx={{ minWidth: "78px" }}>20</ToggleButton>
                            <ToggleButton value={25} selected={selectedCount === 25} onClick={onSelectedCount} sx={{ minWidth: "78px" }}>25</ToggleButton>
                            <ToggleButton value={0} selected={selectedCount === 0} onClick={onSelectedCount} sx={{ minWidth: "78px" }}>{t('CreatePortfolioCustomLabel')}</ToggleButton>
                            {selectedCount === 0 && <NumberInput placeholder={t('CreatePortfolioExactValuePlaceholder')} onChange={onManualCount} value={portfolio.fundCount} />}
                        </Box>
                    </Box>

                    <StageSelector onChange={onDistribChanged} defaultValue={portfolio.stage} />
                </Box>
                <Divider orientation='vertical' />
                <Box flexGrow={1.7} display={"flex"} flexDirection={"column"} flexBasis={"340px"} gap={"45px"}>
                    <Box display={"flex"} flexDirection={"column"}>
                        <Typography fontSize={"16px"} fontWeight={"500"} lineHeight={"19.5px"}>{t('CreatePortfolioInvestorCategory')} <Tooltip title={
                            <Box display={"flex"} flexDirection={"column"}>
                                <Typography fontSize={"12px"} fontWeight={"600"} lineHeight={"18px"} color={theme.palette.primary.dark}>ELTIF 2.0 Compliance</Typography>
                                <Typography fontSize={"12px"} fontWeight={"500"} lineHeight={"18px"}>If you wish to raise funds from retail investors, our system will ensure that your portfolio is constructed in compliance with <Link href="#" color={theme.palette.primary.dark}>ELTIF 2.0 requirements</Link>.</Typography>
                            </Box>}
                            componentsProps={{
                                tooltip: {
                                    style: {
                                        color: theme.palette.common.white,
                                    }
                                }
                            }} arrow placement='bottom-start'>
                            <SvgIcon component={questionIcon} inheritViewBox sx={{ width: "10px", height: "11px", paddingBottom: "2px" }} />
                        </Tooltip>
                        </Typography>
                        <Box display={"flex"}>
                            <FormControlLabel control={<Checkbox checked={portfolio.accreditedInvestors} onChange={onAccreditedInvestorsChanged} />} label={t('CreatePortfolioAccreditedInvestors')} sx={{ typography: { ".MuiFormControlLabel-label": { fontSize: "14px", fontWeight: "400 !important", lineHeight: "20px" } } }} />
                            <FormControlLabel control={<Checkbox checked={portfolio.retailInvestors} onChange={onRetailInvestorsChanged} />} label={t('CreatePortfolioRetailInvestors')} sx={{ typography: { ".MuiFormControlLabel-label": { fontSize: "14px", fontWeight: "400 !important", lineHeight: "20px" } } }} />
                        </Box>
                    </Box>
                    <IndustrySelector onSectorsChanged={onSectorsChanged} value={portfolio.sectors} />
                    <SplitSelector onSplitsChanged={onSplitChanged} value={portfolio.splits} />
                    <CountrySelector onCountriesChanged={onCountriesChanged} value={portfolio.countries} splits={portfolio.splits} />
                </Box>
            </Box>
            <Box display={"flex"} justifyContent={"flex-end"} gap={"12px"}>
                <Button variant='outlined' disabled={blockSubmit} onClick={onStartAi}>
                    {t('CreatePortfolioGenerateWithLeoAiButton')}
                    {isSubmitting === 0 && (<CircularProgress
                        size={24}
                        sx={{
                            color: theme.palette.primary.dark,
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            marginTop: '-12px',
                            marginLeft: '-12px',
                        }}
                    />)}
                </Button>
                <Button variant='outlined' disabled={blockSubmit} onClick={onStartManually}>
                    {t('CreatePortfolioSelectManuallyButton')}
                    {isSubmitting === 1 && (<CircularProgress
                        size={24}
                        sx={{
                            color: theme.palette.primary.dark,
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            marginTop: '-12px',
                            marginLeft: '-12px',
                        }}
                    />)}
                </Button>
                <Button variant='text' disabled={blockSubmit} onClick={onSaveDraft}>
                    {t('CreatePortfolioSaveAsDraftButton')}
                    {isSubmitting === 2 && (<CircularProgress
                        size={24}
                        sx={{
                            color: theme.palette.primary.dark,
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            marginTop: '-12px',
                            marginLeft: '-12px',
                        }}
                    />)}
                </Button>
            </Box>
        </Box>
    )
}

export default CreateFirstStep